<template>
  <div class="flex flex-col h-full flex-1 bg-gray-900">
    <div class="flex-1 flex flex-col pb-4 overflow-y-auto pt-8">
      <RouterLink
        v-if="$currentAccount.logo"
        :to="{ name: 'accounts.show', params: { accountId: $currentAccount.id } }"
      >
        <img :src="$currentAccount.logo" class="block rounded w-24 mb-8 mx-auto" />
      </RouterLink>
      <h6 class="text-center text-white mb-6">
        {{ $currentAccount.name }}
      </h6>
      <nav class="flex-1 bg-gray-900" aria-label="Sidebar">
        <template v-for="(item, routeIdx) in navigation">
          <SidebarNavigationGroup
            v-if="'children' in item"
            :key="`root_item_${routeIdx}`"
            :label="item.label"
            :children="item.children"
          />
          <SidebarNavigationLink
            v-else
            :key="`root_item_${routeIdx}`"
            :route="item.route"
            :label="item.label"
            :exact="item.exact"
            :tip="item.tip"
          />
        </template>
      </nav>
    </div>
  </div>
</template>

<script>
import SidebarNavigationLink from './SidebarNavigationLink.vue';
import SidebarNavigationGroup from './SidebarNavigationGroup.vue';

const availableRoutes = [
  { name: 'accounts.show', params: {}, permissions: [] },
  { name: 'accounts.calendar', params: {}, permissions: ['read contacts'] },
  { name: 'accounts.accountId.prospects', params: {}, permissions: [] },
  { name: 'accounts.accountId.salesFunnel.contacts', params: {}, permissions: [] },
  { name: 'accounts.accountId.customers', params: {}, permissions: [] },
  { name: 'accounts.accountId.contacts', params: {}, permissions: ['read contacts'] },
  { name: 'accounts.accountId.contactMerge', params: {}, permissions: ['edit contacts'] },
  {
    name: 'accounts.accountId.contactRequests',
    params: {},
    permissions: ['read contact requests'],
  },
  { name: 'accounts.pipelines', params: {}, permissions: ['read pipelines'] },
  { name: 'accounts.projects', params: {}, permissions: ['read projects'] },
  { name: 'accounts.channels', params: {}, permissions: ['read channels'] },
  { name: 'accounts.campaigns', params: {}, permissions: ['read campaigns'] },
  { name: 'accounts.accountId.audiences', params: {}, permissions: [] },
  { name: 'accounts.accountId.funnels', params: {}, permissions: [] },
  { name: 'accounts.accountId.emailCampaigns', params: {}, permissions: ['read email campaigns'] },
  { name: 'accounts.accountId.smsCampaigns', params: {}, permissions: ['read sms campaigns'] },
  { name: 'accounts.accountId.accOrders', params: {}, permissions: ['read acc orders'] },
  { name: 'accounts.accountId.clientProducts', params: {}, permissions: [] },
  { name: 'accounts.accountId.assets', params: {}, permissions: ['read assets'] },
  { name: 'accounts.pages', params: {}, permissions: ['read pages'] },
  { name: 'accounts.accountId.emailTemplates', params: {}, permissions: ['read email templates'] },
  { name: 'accounts.accountId.marketingDashboard', params: {}, permissions: [] },
];

const groups = {
  reports: {
    label: 'Reports',
    translationKey: 'shared.reports',
  },
  contacts: {
    label: 'Contacts',
    translationKey: 'shared.contacts',
  },
  requests: {
    label: 'Requests',
    translationKey: 'shared.requests',
  },
  marketing: {
    label: 'Marketing',
    translationKey: 'shared.marketing',
  },
  sales: {
    label: 'Sales',
    translationKey: 'shared.sales',
  },
  library: {
    label: 'Library',
    translationKey: 'shared.library',
  },
};

const availableNavigationItems = [
  {
    label: 'Home',
    translationKey: 'shared.home',
    route: 'accounts.show',
    group: null,
    exact: true,
  },
  { label: 'Calendar', translationKey: 'shared.calendar', route: 'accounts.calendar', group: null },
  {
    label: 'Social media',
    translationKey: 'shared.socialMedia',
    route: 'accounts.accountId.prospects',
    group: 'reports',
  },
  {
    label: 'Contacts',
    translationKey: 'shared.contacts',
    route: 'accounts.accountId.salesFunnel.contacts',
    group: 'reports',
  },
  {
    label: 'Customers',
    translationKey: 'shared.customers',
    route: 'accounts.accountId.customers',
    group: 'reports',
  },
  {
    label: 'All',
    translationKey: 'shared.all',
    route: 'accounts.accountId.contacts',
    group: 'contacts',
  },
  {
    label: 'Manage duplicates',
    translationKey: 'shared.manageDuplicates',
    route: 'accounts.accountId.contactMerge',
    group: 'contacts',
  },
  {
    label: 'All',
    translationKey: 'shared.all',
    route: 'accounts.accountId.contactRequests',
    group: 'requests',
  },
  {
    label: 'Pipelines',
    translationKey: 'shared.pipelines',
    route: 'accounts.pipelines',
    group: 'requests',
  },
  {
    label: 'Dashboard',
    translationKey: 'shared.dashboard',
    route: 'accounts.accountId.marketingDashboard',
    group: 'marketing',
  },
  {
    label: 'Projects',
    translationKey: 'shared.projects',
    route: 'accounts.projects',
    group: 'marketing',
  },
  { label: 'Ads', translationKey: 'shared.ads', route: 'accounts.channels', group: 'marketing' },
  {
    label: 'Ad plans',
    translationKey: 'shared.adPlans',
    route: 'accounts.campaigns',
    group: 'marketing',
  },
  {
    label: 'Ad audiences',
    translationKey: 'shared.adAudiences',
    route: 'accounts.accountId.audiences',
    group: 'marketing',
  },
  {
    label: 'Customer conversions',
    translationKey: 'shared.customerConversions',
    route: 'accounts.accountId.funnels',
    group: 'marketing',
  },
  {
    label: 'Email campaigns',
    translationKey: 'shared.emailCampaigns',
    route: 'accounts.accountId.emailCampaigns',
    group: 'marketing',
  },
  {
    label: 'SMS campaigns',
    translationKey: 'shared.smsCampaigns',
    route: 'accounts.accountId.smsCampaigns',
    group: 'marketing',
  },
  {
    label: 'Orders',
    translationKey: 'shared.orders',
    route: 'accounts.accountId.accOrders',
    group: 'sales',
  },
  {
    label: 'Products',
    translationKey: 'shared.products',
    route: 'accounts.accountId.clientProducts',
    group: 'sales',
  },
  {
    label: 'Assets',
    translationKey: 'shared.assets',
    route: 'accounts.accountId.assets',
    group: 'library',
  },
  {
    label: 'Webpages',
    translationKey: 'shared.webpages',
    route: 'accounts.pages',
    group: 'library',
  },
  {
    label: 'Email templates',
    translationKey: 'shared.emailTemplates',
    route: 'accounts.accountId.emailTemplates',
    group: 'library',
  },
];

const navigationSchema = [
  { type: 'link', name: 'accounts.show' },
  { type: 'link', name: 'accounts.calendar' },
  { type: 'group', name: 'reports' },
  { type: 'group', name: 'contacts' },
  { type: 'group', name: 'requests' },
  { type: 'group', name: 'marketing' },
  { type: 'group', name: 'sales' },
  { type: 'group', name: 'library' },
];

export default {
  components: {
    SidebarNavigationLink,
    SidebarNavigationGroup,
  },
  computed: {
    navigation() {
      const navigationItems = availableNavigationItems
        .filter((navigationItem) => {
          const route = availableRoutes.find((item) => item.name === navigationItem.route);

          if (!route) {
            return false;
          }

          if (
            typeof navigationItem.group === 'string' &&
            navigationItem.group in groups === false
          ) {
            return false;
          }

          if (!route.permissions) {
            return true;
          }

          if (route.permissions.length === 0) {
            return true;
          }

          return this.$membershipHasPermissionTo(route.permissions);
        })
        .reduce((navigation, navigationItem) => {
          const route = availableRoutes.find((item) => item.name === navigationItem.route);

          return [
            ...navigation,
            {
              label: this.$te(navigationItem.translationKey)
                ? this.$t(navigationItem.translationKey)
                : navigationItem.label,
              route: {
                name: navigationItem.route,
                params: {
                  ...route.params,
                  accountId: this.$currentAccount.id,
                },
              },
              tip: this.$isAdminOrSuperAdmin
                ? route.permissions.length
                  ? `Required permissions: ${route.permissions.join(', ')}.`
                  : 'No permissions required.'
                : '',
              exact: !!navigationItem.exact,
              group: navigationItem.group,
            },
          ];
        }, []);

      return navigationSchema.map((item) => {
        if (item.type !== 'link' && item.type !== 'group') {
          return null;
        }

        if (item.type === 'link') {
          return navigationItems.find(
            (navigationItem) =>
              navigationItem.group === null && navigationItem.route.name === item.name
          );
        }

        if (item.name in groups === false) {
          return null;
        }

        const children = navigationItems.filter(
          (navigationItem) => navigationItem.group === item.name
        );

        if (children.length === 0) {
          return null;
        }

        const { [item.name]: group } = groups;
        return {
          label: this.$te(group.translationKey) ? this.$t(group.translationKey) : group.label,
          children,
        };
      });
    },
  },
  methods: {
    translate(keyword, fallback = null) {
      const te = this.$te.bind(this);
      const t = this.$t.bind(this);
      if (te(keyword)) {
        return t(keyword);
      }

      return fallback || keyword;
    },
  },
};
</script>
